import { Observable } from "rxjs";

import { OrdersRepository } from "lib/repositories/index";
import st from "lib/utils/index";
import { createResourceInstance } from "modules/utils/index";
import { pushCompanyRoute, createAlert } from "modules/app/actions";

export const CALL_ORDER_ACTION = "orders/utils/CALL_ORDER_ACTION";
export const CALL_ORDER_ACTION_START = "orders/utils/CALL_ORDER_ACTION_START";
export const CALL_ORDER_ACTION_SUCCESS = "orders/utils/CALL_ORDER_ACTION_SUCCESS";
export const CALL_ORDER_ACTION_FAILURE = "orders/utils/CALL_ORDER_ACTION_FAILURE";
export const CALL_BULK_ORDERS_ACTION = "orders/utils/CALL_BULK_ORDERS_ACTION";
export const CALL_BULK_ORDERS_ACTION_START = "orders/utils/CALL_BULK_ORDERS_ACTION_START";
export const CALL_BULK_ORDERS_ACTION_SUCCESS = "orders/utils/CALL_BULK_ORDERS_ACTION_SUCCESS";
export const CALL_BULK_ORDERS_ACTION_FAILURE = "orders/utils/CALL_BULK_ORDERS_ACTION_FAILURE";
export const SET_ORDER_ACTION_PENDING = "orders/utils/SET_ORDER_ACTION_PENDING";
import { StripeRepository } from "lib/repositories/index";

export const stateSelector = (state) => state.orders.utils;

const callOrderActionResource = createResourceInstance({
  FETCH: CALL_ORDER_ACTION,
  FETCH_START: CALL_ORDER_ACTION_START,
  FETCH_SUCCESS: CALL_ORDER_ACTION_SUCCESS,
  FETCH_FAILURE: CALL_ORDER_ACTION_FAILURE,
  stateSelector,
  stateKey: "callOrderAction",
  withFailureAlert: true,
  getModel: (state, store, { orderId, orderToken, actionName }) => {
    return OrdersRepository[actionName](orderId, orderToken);
  },
});

const callBulkOrdersActionResource = createResourceInstance({
  FETCH: CALL_BULK_ORDERS_ACTION,
  FETCH_START: CALL_BULK_ORDERS_ACTION_START,
  FETCH_SUCCESS: CALL_BULK_ORDERS_ACTION_SUCCESS,
  FETCH_FAILURE: CALL_BULK_ORDERS_ACTION_FAILURE,
  stateSelector,
  stateKey: "callBulkOrdersAction",
  withFailureAlert: true,
  getModel: (state, store, { ordersIds, actionName }) => {
    return OrdersRepository[actionName](ordersIds);
  },
});

const initialState = {
  callOrderAction: callOrderActionResource.initialState,
  callBulkOrdersAction: callBulkOrdersActionResource.initialState,
  orderActionPendingById: {},
};

const reducers = {
  ...callOrderActionResource.reducersMap,
  ...callBulkOrdersActionResource.reducersMap,

  [SET_ORDER_ACTION_PENDING]: (state, { orderId, fetchLoading }) => ({
    ...state,
    orderActionPendingById: {
      ...state.orderActionPendingById,
      [orderId]: fetchLoading,
    },
  }),
};

const epics = [
  ...callOrderActionResource.epics,
  ...callBulkOrdersActionResource.epics,

  (actions$, { getState }) =>
    actions$
      .ofType(CALL_ORDER_ACTION_START)
      .merge(actions$.ofType(CALL_ORDER_ACTION_SUCCESS))
      .merge(actions$.ofType(CALL_ORDER_ACTION_FAILURE))
      .flatMap(({ params: { orderId } }) => {
        const {
          callOrderAction: { fetchLoading },
        } = stateSelector(getState());
        return Observable.of({
          type: SET_ORDER_ACTION_PENDING,
          orderId,
          fetchLoading,
        });
      }),

  (actions$) =>
    actions$.ofType(CALL_ORDER_ACTION_SUCCESS).flatMap(({ model, params: { actionName } }) => {
      if (actionName === "redeliverCustomerOrderEmail") {
        return Observable.of(
          createAlert({
            type: "success",
            content: I18n.t("papp.orders.utils.redeliver_customer_order_email_success_message"),
          })
        );
      } else if (actionName === "redeliverCustomerOrderOpinionReminderEmail") {
        return Observable.of(
          createAlert({
            type: "success",
            content: I18n.t("papp.orders.utils.redeliver_customer_order_opinion_reminder_email_success_message"),
          })
        );
      } else if (actionName === "redeliverSupplierOrderEmail") {
        return Observable.of(
          createAlert({
            type: "success",
            content: I18n.t("papp.orders.utils.redeliver_supplier_order_email_success_message"),
          })
        );
      } else {
        return Observable.empty();
      }
    }),

  (actions$) =>
    actions$.ofType(CALL_BULK_ORDERS_ACTION_SUCCESS).flatMap(({ model, params: { actionName } }) => {
      if (actionName === "createAgentDisbursement") {
        return Observable.of(
          createAlert({
            type: "success",
            content: I18n.t("papp.orders.utils.create_agent_disbursement_success_message", {
              agent_disbursement_id: model.agent_disbursement.id,
            }),
          })
        );
      } else if (actionName === "createPlatformDisbursement") {
        // A run can settle in either direction - a payout to the supplier, or the platform fee
        // collected back from it - and it books nothing at all when there is no money left to
        // move between Tripsomnia and the supplier. That last case is a settlement too: the
        // backend marks those orders cleared, so they are reported as settled rather than
        // passed over. It covers a zero balance and a deposit order alike - there the supplier
        // stays owed the rest the customer pays them on site, which never passes through us.
        // Only the underfunded ones stay open - money is still due to arrive at the platform
        // to cover what the supplier is owed - which is why they get their own line.
        //
        // `skipped_orders_ids` is the backend's name for the zero-balance orders; it predates
        // the underfunded bucket being split out of it.
        const settledCount = model.order_transfers.length;
        const collectedCount = (model.collected_order_transfers || []).length;
        const nothingToSettleCount = (model.skipped_orders_ids || []).length;
        const underfundedCount = (model.underfunded_orders_ids || []).length;

        const messages = [
          [settledCount, "create_platform_disbursement_success_message"],
          [collectedCount, "create_platform_disbursement_collected_message"],
          [nothingToSettleCount, "create_platform_disbursement_nothing_to_settle_message"],
          [underfundedCount, "create_platform_disbursement_underfunded_message"],
        ]
          .filter(([count]) => count > 0)
          .map(([count, key]) => I18n.t(`papp.orders.utils.${key}`, { count }));

        return Observable.of(
          createAlert({
            // An order left open is the only outcome that needs the operator back: a run that
            // only cleared zero-balance orders booked nothing, but finished the job.
            type: underfundedCount === 0 ? "success" : "warning",
            // Four sentences of counts, and the only place a settlement run is ever reported.
            // It needs longer on screen than the app-wide default gives a one-line confirmation.
            timeout: 20000,
            content: messages.join(" "),
          })
        );
      } else {
        return Observable.empty();
      }
    }),
];

import { fetchOrder } from "modules/orders/ShowOrderView/index";
export function goToOrder(orderId, orderToken, { refresh = false } = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const ShowOrderViewState = state.orders.ShowOrderView;
    if (ShowOrderViewState.order && ShowOrderViewState.order.id === orderId && refresh) {
      dispatch(fetchOrder(orderId, orderToken));
    } else {
      dispatch(pushCompanyRoute(`/orders/${orderId}/${orderToken}`));
    }
  };
}

export function processPayment(orderId, orderToken, o) {
  return (dispatch, getState) => {
    const state = getState();
    const ShowOrderViewState = state.orders.ShowOrderView;
    StripeRepository.paymentOperations(orderId, orderToken, o);
    if (ShowOrderViewState.order && ShowOrderViewState.order.id === orderId && refresh) {
      dispatch(fetchOrder(orderId, orderToken));
    } else {
      dispatch(pushCompanyRoute(`/orders/${orderId}/${orderToken}`));
    }
  };
}

export const { fetch: fetchCallOrderAction } = callOrderActionResource.actions;
export function callOrderAction(order, actionName) {
  return fetchCallOrderAction({
    orderId: order.id,
    orderToken: order.token,
    actionName,
  });
}

export const { fetch: fetchCallBulkOrdersAction } = callBulkOrdersActionResource.actions;
export function callBulkOrdersAction(ordersIds, actionName) {
  return fetchCallBulkOrdersAction({ ordersIds, actionName });
}

export function confirmOrder(order) {
  return callOrderAction(order, "confirm");
}

export function approveOrder(order) {
  return callOrderAction(order, "approve");
}
export function approveOrderWithConfirm(order) {
  return (dispatch) => {
    return st
      .confirm({ content: I18n.t("papp.orders.utils.confirm_approve_message") })
      .then((yes) => yes && dispatch(approveOrder(order)));
  };
}

export function rejectOrder(order) {
  return callOrderAction(order, "reject");
}
export function rejectOrderWithConfirm(order) {
  return (dispatch) => {
    return st
      .confirm({ content: I18n.t("papp.orders.utils.confirm_reject_message") })
      .then((yes) => yes && dispatch(rejectOrder(order)));
  };
}

export function finishOrder(order) {
  return callOrderAction(order, "finish");
}
export function finishOrderWithConfirm(order) {
  return (dispatch) => {
    return st
      .confirm({ content: I18n.t("papp.orders.utils.confirm_finish_message") })
      .then((yes) => yes && dispatch(finishOrder(order)));
  };
}

export function finishOrderWithOpinionRequest(order) {
  return callOrderAction(order, "finishWithOpinionRequest");
}
export function finishOrderWithCallback(order, shouldSendOpinionRequest) {
  return (dispatch) => {
    if (shouldSendOpinionRequest) {
      dispatch(finishOrderWithOpinionRequest(order));
    } else {
      dispatch(finishOrder(order));
    }
  };
}

export function cancelOrder(order) {
  return callOrderAction(order, "cancel");
}
export function cancelOrderWithConfirm(order) {
  return (dispatch) => {
    return st
      .confirm({ content: I18n.t("papp.orders.utils.confirm_cancel_message") })
      .then((yes) => yes && dispatch(cancelOrder(order)));
  };
}

export function deleteOrder(order) {
  return callOrderAction(order, "delete");
}
export function deleteOrderWithConfirm(order) {
  return (dispatch) => {
    return st
      .confirm({ content: I18n.t("papp.orders.utils.confirm_delete_message") })
      .then((yes) => yes && dispatch(deleteOrder(order)));
  };
}

import { open as openNewOrderModal } from "modules/orders/NewOrderModal";
export function redoOrder(order) {
  return openNewOrderModal(order);
}

export function redeliverCustomerOrderEmail(order) {
  return callOrderAction(order, "redeliverCustomerOrderEmail");
}

export function redeliverCustomerOrderOpinionReminderEmail(order) {
  return callOrderAction(order, "redeliverCustomerOrderOpinionReminderEmail");
}

export function redeliverSupplierOrderEmail(order) {
  return callOrderAction(order, "redeliverSupplierOrderEmail");
}

export function createOrdersAgentDisbursement(ordersIds) {
  return callBulkOrdersAction(ordersIds, "createAgentDisbursement");
}

export function createOrdersPlatformDisbursement(ordersIds) {
  return callBulkOrdersAction(ordersIds, "createPlatformDisbursement");
}

export default st.duck.createDuck(initialState, reducers, epics);



// WEBPACK FOOTER //
// ./src/modules/orders/utils.js